import { useEffect, useState } from 'react';
import {
  collectionGroup,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  collection,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  AlertDoc,
  AlertingConfigDoc,
  AuditLogEntry,
  ChannelDoc,
  KeywordValidationConfig,
  SessionDoc,
} from '@monitor/shared';
import { db, functions } from './firebase';

export interface ChannelWithId extends ChannelDoc {
  id: string;
}

export interface AlertWithId extends AlertDoc {
  id: string;
}

/**
 * Live listener over every channel (collectionGroup). Pass `enabled=false`
 * until auth has a signed-in user: a listen sent unauthenticated is rejected
 * by the rules, and a listener error TERMINATES the subscription — it would
 * never restart after sign-in, leaving the board on a false "no channels"
 * state (the fresh-login bug). Gating on auth (re)subscribes when `enabled`
 * flips true.
 */
export function useChannels(enabled: boolean): { channels: ChannelWithId[]; loading: boolean } {
  const [channels, setChannels] = useState<ChannelWithId[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    const q = query(collectionGroup(db, 'channels'));
    return onSnapshot(
      q,
      (snap) => {
        setChannels(snap.docs.map((d) => ({ id: d.id, ...(d.data() as ChannelDoc) })));
        setLoading(false);
      },
      // A listener error terminates the subscription without ever firing the
      // success callback — resolve loading so the board can't spin forever.
      () => setLoading(false),
    );
  }, [enabled]);
  return { channels, loading };
}

/**
 * Live listener over alerts, newest first. Gated on `enabled` for the same
 * fresh-login reason as `useChannels`: an unauthenticated listen is rejected by
 * the rules and its error terminates the subscription for good. `orderBy(
 * createdAt).limit()` is a single-field order Firestore auto-indexes — no
 * composite index needed (KTD2). The R8 actionable set is derived client-side
 * from this list in the view.
 */
export function useAlerts(enabled: boolean, max = 50): { alerts: AlertWithId[]; loading: boolean } {
  const [alerts, setAlerts] = useState<AlertWithId[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    const q = query(collection(db, 'alerts'), orderBy('createdAt', 'desc'), limit(max));
    return onSnapshot(
      q,
      (snap) => {
        setAlerts(snap.docs.map((d) => ({ id: d.id, ...(d.data() as AlertDoc) })));
        setLoading(false);
      },
      // A listener error never fires the success callback — resolve loading so
      // the view can't spin forever.
      () => setLoading(false),
    );
  }, [enabled, max]);
  return { alerts, loading };
}

/**
 * Flip an alert's `acknowledged` flag. Writes only that field, satisfying the
 * rules' `hasOnly(['acknowledged'])` constraint (R5). For an agent_down alert
 * whose delivery failed, this releases (true) or re-arms (false) the dead-man
 * switch — the view gates that case behind a confirmation step.
 */
export async function acknowledgeAlert(alertId: string, acknowledged: boolean): Promise<void> {
  await updateDoc(doc(db, 'alerts', alertId), { acknowledged });
}

/**
 * Live listener over the *actionable* set: unacknowledged agent_down alerts whose
 * delivery failed — the alerts still arming the dead-man switch (R8). This is the
 * exact query the backend's `hasUndeliveredAgentDown` runs, and it is served by
 * the existing `(failureClass, deliveryFailed, acknowledged)` composite index —
 * so it needs no new index and no `orderBy` (the caller sorts the small set
 * client-side). Kept separate from `useAlerts` on purpose: that list is capped at
 * the newest N, which could drop an old arming alert past the window and leave the
 * operator no way to acknowledge it.
 */
export function useActionableAlerts(enabled: boolean): AlertWithId[] {
  const [alerts, setAlerts] = useState<AlertWithId[]>([]);
  useEffect(() => {
    if (!enabled) return;
    const q = query(
      collection(db, 'alerts'),
      where('failureClass', '==', 'agent_down'),
      where('deliveryFailed', '==', true),
      where('acknowledged', '==', false),
    );
    return onSnapshot(
      q,
      (snap) => setAlerts(snap.docs.map((d) => ({ id: d.id, ...(d.data() as AlertDoc) }))),
      () => setAlerts([]),
    );
  }, [enabled]);
  return alerts;
}

/** Recent sessions for a channel, newest first. */
export function useSessions(channelId: string, max = 25): SessionDoc[] {
  const [sessions, setSessions] = useState<SessionDoc[]>([]);
  useEffect(() => {
    const q = query(
      collection(db, 'sessions'),
      where('channelId', '==', channelId),
      orderBy('createdAt', 'desc'),
      limit(max),
    );
    return onSnapshot(q, (snap) => setSessions(snap.docs.map((d) => d.data() as SessionDoc)));
  }, [channelId, max]);
  return sessions;
}

/** Config-change audit trail, newest first. */
export function useAuditLog(max = 50): AuditLogEntry[] {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  useEffect(() => {
    const q = query(collection(db, 'admin/auditLog/entries'), orderBy('createdAt', 'desc'), limit(max));
    return onSnapshot(
      q,
      (snap) => setEntries(snap.docs.map((d) => d.data() as AuditLogEntry)),
      () => setEntries([]),
    );
  }, [max]);
  return entries;
}

export interface ChannelConfigPatch {
  frequencyMinutes?: number;
  thresholdMinutes?: number;
  graceMinutes?: number;
  probeTemplate?: string;
  scriptedReplies?: string[];
  validation?: KeywordValidationConfig;
}

/** Write validated config through the rules-guarded update path (audit auto-generated). */
export async function updateChannelConfig(productId: string, channelId: string, patch: ChannelConfigPatch): Promise<void> {
  await updateDoc(doc(db, `products/${productId}/channels/${channelId}`), patch as Record<string, unknown>);
}

export async function pauseChannel(
  productId: string,
  channelId: string,
  resumeAtMs: number | null,
): Promise<void> {
  await updateDoc(doc(db, `products/${productId}/channels/${channelId}`), {
    paused: true,
    resumeAt: resumeAtMs ? Timestamp.fromMillis(resumeAtMs) : null,
  });
}

export async function resumeChannel(productId: string, channelId: string): Promise<void> {
  await updateDoc(doc(db, `products/${productId}/channels/${channelId}`), { paused: false, resumeAt: null });
}

/** Live listener on the alert-routing config (R11). */
export function useAlertingConfig(): AlertingConfigDoc | null {
  const [cfg, setCfg] = useState<AlertingConfigDoc | null>(null);
  useEffect(
    () =>
      onSnapshot(doc(db, 'admin/alerting'), (snap) =>
        setCfg(snap.exists() ? (snap.data() as AlertingConfigDoc) : null),
      ),
    [],
  );
  return cfg;
}

export async function updateAlertingConfig(patch: Partial<AlertingConfigDoc>): Promise<void> {
  await updateDoc(doc(db, 'admin/alerting'), patch);
}

/** Trigger a manual check via the callable — same transactional launch as the tick. */
export async function runCheckNow(productId: string, channelId: string): Promise<{ launched: boolean; message?: string }> {
  const fn = httpsCallable<{ productId: string; channelId: string }, { launched: boolean; message?: string }>(
    functions,
    'runCheckNow',
  );
  const res = await fn({ productId, channelId });
  return res.data;
}
